<script lang="ts">
  import { Dialog, DialogContent, DialogTitle } from "$lib/components/ui/dialog";
  import { locale } from "$lib/i18n";
  import { overlay, helpSectionId } from "$lib/stores/ui";

  const isOpen = overlay('help');

  let sections: Record<string, string> | null = $state(null);
  let loadedLocale = $state('');
  let contentEl: HTMLElement | undefined = $state();
  let lightboxSrc = $state('');
  let lightboxAlt = $state('');

  // Fetch sections JSON once per locale, lazily.
  $effect(() => {
    if ($isOpen && loadedLocale !== $locale) {
      fetch(`/manual/sections.${$locale}.json`)
        .then(r => r.json())
        .then((data: Record<string, string>) => {
          sections = data;
          loadedLocale = $locale;
        });
    }
  });

  // Clear the lightbox when the help dialog is closed.
  $effect(() => {
    if (!$isOpen) lightboxSrc = '';
  });

  // Focus the scroll area and wire up thumbnail click handlers after each render.
  $effect(() => {
    void sectionHtml;
    if (!contentEl || !$isOpen) return;

    contentEl.focus();

    const links = contentEl.querySelectorAll<HTMLAnchorElement>('a.thumb-link');
    const handlers: Array<{ el: HTMLAnchorElement; fn: (e: Event) => void }> = [];
    links.forEach(link => {
      const fn = (e: Event) => {
        e.preventDefault();
        lightboxSrc = link.dataset.src ?? '';
        lightboxAlt = link.dataset.alt ?? '';
      };
      link.addEventListener('click', fn);
      handlers.push({ el: link, fn });
    });
    return () => handlers.forEach(({ el, fn }) => el.removeEventListener('click', fn));
  });

  // Intercept Escape when lightbox is open so it closes only the lightbox.
  $effect(() => {
    if (!lightboxSrc) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); lightboxSrc = ''; }
    };
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  });

  const sectionHtml = $derived(
    sections && $helpSectionId ? (sections[$helpSectionId] ?? '') : ''
  );
</script>

<Dialog bind:open={$isOpen}>
  <DialogContent class="sm:max-w-2xl flex flex-col max-h-[80vh] z-[100]" overlayClass="z-[99]" aria-describedby={undefined}>
    <DialogTitle class="sr-only">Help</DialogTitle>
    <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
    <div
      bind:this={contentEl}
      class="help-content flex-1 min-h-0 overflow-y-auto px-1 outline-none"
      tabindex="0"
    >
      {@html sectionHtml}
    </div>

    {#if lightboxSrc}
      <!-- Rendered inside DialogContent so Bits UI's focus trap doesn't mark it inert.
           position:fixed is always relative to the viewport, so inset:0 still covers the full screen. -->
      <div
        role="presentation"
        style="position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,0.85);display:flex;align-items:center;justify-content:center;cursor:zoom-out"
        onclick={() => { lightboxSrc = ''; }}
      >
        <img
          src={lightboxSrc}
          alt={lightboxAlt}
          style="max-width:90vw;max-height:90vh;object-fit:contain;border-radius:6px;box-shadow:0 12px 48px rgba(0,0,0,.6);cursor:zoom-out"
        />
        <button
          aria-label="Close"
          style="position:absolute;top:1rem;right:1.5rem;background:none;border:none;color:#fff;font-size:2.25rem;line-height:1;cursor:pointer;opacity:0.75;padding:0"
          onmouseenter={(e) => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
          onmouseleave={(e) => { (e.currentTarget as HTMLElement).style.opacity = '0.75'; }}
          onclick={(e) => { e.stopPropagation(); lightboxSrc = ''; }}
        >&times;</button>
      </div>
    {/if}
  </DialogContent>
</Dialog>

<style>
  .help-content :global(h2),
  .help-content :global(h3),
  .help-content :global(h4) {
    font-weight: 600;
    margin: 0.25rem 0 0.5rem;
    line-height: 1.3;
  }
  .help-content :global(h2) { font-size: 1.2rem; }
  .help-content :global(h3) { font-size: 1.05rem; color: #334155; }
  .help-content :global(h4) { font-size: 0.95rem; color: #475569; }
  .help-content :global(p)  { margin: 0.5rem 0; font-size: 0.9rem; line-height: 1.6; }
  .help-content :global(hr) { border: none; border-top: 1px solid #e2e8f0; margin: 1rem 0; }
  .help-content :global(ul),
  .help-content :global(ol) { padding-left: 1.4rem; margin: 0.4rem 0; font-size: 0.9rem; }
  .help-content :global(li) { margin: 0.2rem 0; }
  .help-content :global(strong) { font-weight: 600; }
  .help-content :global(em)     { font-style: italic; }
  .help-content :global(code)   { background: #f1f5f9; padding: 0.1em 0.3em; border-radius: 3px; font-size: 0.85em; font-family: ui-monospace, monospace; }
  .help-content :global(blockquote) {
    border-left: 3px solid #94a3b8;
    margin: 0.75rem 0;
    padding: 0.4rem 1rem;
    background: #f8fafc;
    color: #475569;
    border-radius: 0 4px 4px 0;
    font-size: 0.88rem;
  }
  .help-content :global(blockquote p) { margin: 0; }
  .help-content :global(table) {
    border-collapse: collapse;
    width: 100%;
    margin: 0.5rem 0;
    font-size: 0.875rem;
  }
  .help-content :global(th),
  .help-content :global(td) {
    border: 1px solid #cbd5e1;
    padding: 0.35rem 0.6rem;
    text-align: left;
  }
  .help-content :global(th) { background: #f8fafc; font-weight: 600; }
  .help-content :global(tr:nth-child(even) td) { background: #fafafa; }
  .help-content :global(figure.thumb-fig) {
    display: block;
    margin: 0.5rem 0;
    text-align: center;
  }
  .help-content :global(figure.thumb-fig img) {
    display: inline-block; /* override Tailwind preflight's img { display: block } */
    max-width: 40%;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    cursor: zoom-in;
    transition: box-shadow 0.15s, border-color 0.15s;
  }
  .help-content :global(figure.thumb-fig img:hover) {
    border-color: #94a3b8;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
  .help-content :global(figcaption) {
    font-size: 0.78rem;
    color: #64748b;
    margin-top: 0.25rem;
  }
</style>
